// Derives a table's structure from its authored markup.
//
// The stored `html` is the single source of truth, so the header row and the
// caption are NOT stored as structure — they are applied here, once, and the
// result is handed to the same `renderContentHtml` pipeline every other content
// unit uses. Screen and PDF therefore cannot diverge: they call this.
//
// The editor (TipTap) writes `<table><tbody><tr><th>…` with no `<thead>` and no
// `scope`, and an API author may write anything. Both end up as the same
// accessible markup here.

import { shouldRenderCaptionAsHtml } from './imageAltText';

export interface TableMarkupOptions {
  // The unit's caption, rendered as the table's <caption> so a screen reader
  // announces it as the table's name rather than as loose text after it.
  caption?: string;
  // Whether the first row is a header.
  headerRow: boolean;
}

function convertCell(
  cell: Element,
  tagName: 'td' | 'th',
  doc: Document,
): Element {
  if (cell.tagName.toLowerCase() === tagName) {
    return cell;
  }

  const replacement = doc.createElement(tagName);

  for (const attribute of Array.from(cell.attributes)) {
    replacement.setAttribute(attribute.name, attribute.value);
  }

  while (cell.firstChild) {
    replacement.appendChild(cell.firstChild);
  }

  cell.replaceWith(replacement);
  return replacement;
}

function getFirstBodyOrCreate(table: HTMLTableElement, doc: Document) {
  const existing = table.tBodies[0];

  if (existing) {
    return existing;
  }

  const tbody = doc.createElement('tbody');
  table.appendChild(tbody);
  return tbody;
}

/**
 * The first child a `<thead>` must precede — a row group or a bare row — or
 * null to append. Anything before that point (caption, colgroup) has to stay
 * where the author put it.
 */
function firstRowGroup(table: HTMLTableElement): Element | null {
  for (const child of Array.from(table.children)) {
    if (['tbody', 'tfoot', 'tr'].includes(child.tagName.toLowerCase())) {
      return child;
    }
  }

  return null;
}

function removeEmptySections(table: HTMLTableElement) {
  for (const section of Array.from(
    table.querySelectorAll(':scope > thead, :scope > tbody, :scope > tfoot'),
  )) {
    if (section.children.length === 0) {
      section.remove();
    }
  }
}

function applyHeaderRow(
  table: HTMLTableElement,
  headerRow: boolean,
  doc: Document,
) {
  // `rows` covers the table's own rows across thead/tbody/tfoot and — unlike a
  // `querySelector('tr')` — never reaches into a nested table.
  const firstRow = table.rows[0];

  if (!firstRow) {
    return;
  }

  if (!headerRow) {
    for (const cell of Array.from(firstRow.cells)) {
      const converted = convertCell(cell, 'td', doc);
      converted.removeAttribute('scope');
    }

    // A <thead> with ordinary cells still announces them as headers, so the
    // section has to go too, not just the tags.
    const thead = table.tHead;

    if (thead) {
      const tbody = getFirstBodyOrCreate(table, doc);

      while (thead.firstChild) {
        tbody.insertBefore(thead.firstChild, tbody.firstChild);
      }

      thead.remove();
    }

    removeEmptySections(table);
    return;
  }

  for (const cell of Array.from(firstRow.cells)) {
    const converted = convertCell(cell, 'th', doc);
    // Without scope, a screen reader has to guess whether a header labels its
    // column or its row.
    converted.setAttribute('scope', 'col');
  }

  let thead = table.tHead;

  if (!thead) {
    thead = doc.createElement('thead');
    // A table's children are ordered caption?, colgroup*, thead?, then the row
    // groups — so a new thead goes before the first row group, NOT at
    // firstChild. Inserting at firstChild displaced an authored <caption> out
    // of the first position (where it is the table's accessible name) and
    // pushed a <colgroup> after the thead.
    table.insertBefore(thead, firstRowGroup(table));
  }

  if (firstRow.parentElement !== thead) {
    thead.appendChild(firstRow);
  }

  removeEmptySections(table);
}

function applyCaption(
  table: HTMLTableElement,
  caption: string | undefined,
  doc: Document,
) {
  const text = caption?.trim();

  if (!text) {
    return;
  }

  // An authored <caption> wins — the author was explicit.
  if (table.caption) {
    return;
  }

  const element = doc.createElement('caption');

  // Markup only when the caption actually contains a tag, matching CaptionText.
  // Whatever lands here is sanitized downstream by renderContentHtml, which is
  // what makes assigning innerHTML safe; textContent escapes the rest so a
  // caption like `Ratio A<B` keeps its angle bracket.
  if (shouldRenderCaptionAsHtml(text)) {
    element.innerHTML = text;
  } else {
    element.textContent = text;
  }
  // <caption> is only valid as the table's first child.
  table.insertBefore(element, table.firstChild);
}

/**
 * Rewrites the first row to match the header-row setting, without touching
 * anything else.
 *
 * The editor calls this when the toggle changes so the grid an instructor is
 * looking at matches what students will see; the renderer calls it through
 * `buildTableMarkup`. One implementation, so the two cannot disagree.
 */
export function applyTableHeaderRow(html: string, headerRow: boolean): string {
  if (typeof DOMParser === 'undefined') {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = doc.body.querySelector('table');

  if (!table) {
    return html;
  }

  applyHeaderRow(table as HTMLTableElement, headerRow, doc);
  return doc.body.innerHTML;
}

/**
 * Applies the unit's settings to its authored table markup.
 *
 * Returns the markup unchanged when there is no table to work on or when
 * DOMParser is unavailable, so a malformed body still renders as best it can
 * rather than disappearing.
 */
export function buildTableMarkup(
  html: string,
  { caption, headerRow }: TableMarkupOptions,
): string {
  if (typeof DOMParser === 'undefined') {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = doc.body.querySelector('table');

  if (!table) {
    return html;
  }

  applyHeaderRow(table as HTMLTableElement, headerRow, doc);
  applyCaption(table as HTMLTableElement, caption, doc);

  return doc.body.innerHTML;
}

/**
 * Counts the table's widest row, for the editor's "hard to read on a phone"
 * warning. Colspans count as the columns they occupy.
 */
export function countTableColumns(html: string): number {
  if (typeof DOMParser === 'undefined') {
    return 0;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = doc.body.querySelector('table') as HTMLTableElement | null;

  if (!table) {
    return 0;
  }

  let widest = 0;

  for (const row of Array.from(table.rows)) {
    const width = Array.from(row.cells).reduce(
      (total, cell) => total + Math.max(1, cell.colSpan || 1),
      0,
    );
    widest = Math.max(widest, width);
  }

  return widest;
}
